import { LanguageManagementAPI, ProfileAdministrationAPI, ProfileContractorAPI } from "../api";
import { useProductsWithRolesStore } from "../stores/productsWithRoles";
import { useProductsWithDepartmentsStore } from "../stores/productsWithDepartments";
import {
    Department,
    Identifier,
    Participant,
    ParticipantActivityType,
    Product,
    Project,
    ProjectRole,
    Role,
    Trainer,
} from "../types/PersologOnlineTypes";
import { sendQuestionnaireEmail } from "./EmailUtils";
import { i18n } from "../main.ts";

/**
 * Mapping of product numbers to focus keys
 */
export const focusMapping: { [key: string]: string } = {
    in129: "feedback",
    it129: "feedback",
    in120: "job-match",
    in122: "job-match",
    in102: "job",
    in116: "job",
    pl116: "focus-person",
    pl120: "team",
    pl460: "team",
    pl802: "subject",
    default: "focus",
};

export const BENCHMARK_BASIC = "in120";
export const BENCHMARK_JOBMATCH = "in122";
export const SHORTPROFILE_FOR_361 = "in129";
export const SHORTPROFILE_FOR_361_ITALIAN = "it129";
export const GROUP_CHART_FOR_361 = "in119";
export const GROUP_CHART_FOR_361_ITALIAN = "it119";
/**
 * Toast group of a project's group chart panel. The project page mounts several <Toast />s and each ungrouped one
 * shows every ungrouped message, so without a group the panel's toasts appeared once per mounted Toast.
 */
export const groupChartToastGroup = (projectId?: number) => `group-chart-${projectId}`;
export const HEXACO_GROUP_CHART = "in1002";
export const ORGA_RESILIENCE = "in752";
export const TEAM_RESILIENCE = "in755";
export const JOB_PROFILE = "in102";
export const JOB_PROFILE_SHORT = "in116";

const roleDefaultNamesCache: Record<string, Record<string, string>> = {};

/**
 * Picks the appropriate text for an identifier based on the specified language,
 * with fallbacks to English and then any available text.
 * @param identifier
 * @param language
 * @returns
 */
function pickTextByLanguage(identifier: Identifier, language: string): string | undefined {
    const exact = identifier.texts.find((t) => t.language === language)?.text;
    if (exact) return exact;

    const en = identifier.texts.find((t) => t.language === "en")?.text;
    if (en) return en;

    return identifier.texts[0]?.text;
}

function resolveDefaultRoleName(defaultNames: Record<string, string>, roleType: string): string {
    return (
        defaultNames[roleType] ??
        defaultNames[roleType.toLowerCase()] ??
        defaultNames[roleType.toUpperCase()] ??
        roleType
    );
}

function buildRolePayloadWithCustomNames(
    roles: ProjectRole[],
    defaultNames: Record<string, string>,
): ProjectRole[] {
    return roles.map((role) => {
        const trimmedName = role.name?.trim() ?? "";
        const defaultName = resolveDefaultRoleName(defaultNames, role.role).trim();
        const isCustomized = trimmedName !== "" && trimmedName !== defaultName;

        return {
            ...role,
            name: isCustomized ? role.name : "",
        };
    });
}

export async function fetchDefaultRoleNames(project: Project): Promise<Record<string, string>> {
    return fetchDefaultRoleNamesForProduct(project.defaultProduct, String(project.language));
}

export async function fetchDefaultRoleNamesForProduct(
    defaultProduct: string,
    language: string,
): Promise<Record<string, string>> {
    const textProduct = `questionnaire-${defaultProduct}`;
    const cacheKey = `${textProduct}|${language}`;

    if (roleDefaultNamesCache[cacheKey]) {
        return roleDefaultNamesCache[cacheKey];
    }

    try {
        const identifiers = await LanguageManagementAPI.fetchTexts(textProduct);
        const roleNames: Record<string, string> = {};

        for (const identifier of identifiers) {
            const prefix = `${defaultProduct}.role.`;
            if (!identifier.identifier.startsWith(prefix)) continue;

            const roleType = identifier.identifier.slice(prefix.length);
            const localizedText = pickTextByLanguage(identifier, language);
            if (localizedText) {
                roleNames[roleType] = localizedText;
                roleNames[roleType.toLowerCase()] = localizedText;
                roleNames[roleType.toUpperCase()] = localizedText;
            }
        }

        roleDefaultNamesCache[cacheKey] = roleNames;
        return roleNames;
    } catch (error) {
        console.error(`Error fetching default role names for ${textProduct}:`, error);
        return {};
    }
}

export async function ensureProductsWithDepartmentsLoaded(): Promise<void> {
    const store = useProductsWithDepartmentsStore();
    await store.ensureLoaded();
}

const isPpmProduct = (productNo: string): boolean => {
    const store = useProductsWithDepartmentsStore();
    return store.isPpmProduct(productNo);
};

/**
 * Determines if a product has departments based on its product number.
 * 'productNeedsDepartmentsTab' is a subset of this, as there are products that have departments but do not need the departments tab to be shown.
 * @param productNo 
 * @returns 
 */
export function productHasDepartments(productNo: string): boolean {
    return productNo === ORGA_RESILIENCE || productNo === TEAM_RESILIENCE || isPpmProduct(productNo);
}

/**
 * Determines if a product needs the departments tab based on its product number. 
 * @param productNo 
 * @returns 
 */
export function productNeedsDepartmentsTab(productNo: string): boolean {
    return productNo === ORGA_RESILIENCE || productNo === TEAM_RESILIENCE;
}

export function productHasRoles(productNo: string): Promise<boolean> {
    const store = useProductsWithRolesStore();
    return store.ensureLoaded().then(() => store.productHasRoles(productNo));
}

export function productHasBenchmarkDiagram(productNo: string) {
    return productNo === BENCHMARK_BASIC || productNo === "in121" || productNo === BENCHMARK_JOBMATCH;
}

export function hasGroupChart(project: Project): Promise<boolean> {
    return getGroupChartForProduct(project.product)
        .then((groupChart) => groupChart !== "")
        .catch((error) => {
            console.error(`Error fetching group chart for project ${project.product}:`, error);
            return false;
        });
}

export async function getGroupChartForProduct(product: string): Promise<string> {
    const charts = await ProfileAdministrationAPI.getGroupChartForProduct(product);
    return charts.length > 0 ? charts[0].groupChart : "";
}

/**
 * Returns the available quantity of a product for a trainer
 * @param trainerId
 * @param productId
 * @returns available quantity of the product for the trainer
 */
export async function getProductQuantity(trainerId: string, productId: string) {
    const purchases = await ProfileAdministrationAPI.fetchPurchasedProducts(trainerId);
    const filteredPurchases = purchases.filter((purchase) => purchase.product === productId);
    if (filteredPurchases.length === 0) {
        return 0;
    } else {
        return filteredPurchases[0].quantity;
    }
}

export async function getProduct(trainerId: string, productId: string): Promise<Product | undefined> {
    const purchases = await ProfileAdministrationAPI.fetchAllProductsForTrainer(trainerId);
    return purchases.find((purchase) => purchase.productNo === productId);
}

export async function getDepartments(project: Project): Promise<Department[]> {
    await ensureProductsWithDepartmentsLoaded();
    if (project.id && productHasDepartments(project.product)) {
        return await ProfileAdministrationAPI.getDepartments(project.id);
    } else {
        return [];
    }
}

export async function saveDepartments(project: Project, departments: Department[]) {
    if (project.id) {
        return await ProfileAdministrationAPI.updateDepartments(project.id, departments);
    }
}

export async function saveRoles(project: Project, roles: ProjectRole[]) {
    if (project.id) {
        const defaultNames = await fetchDefaultRoleNames(project);
        const payload = buildRolePayloadWithCustomNames(roles, defaultNames);
        return await ProfileAdministrationAPI.updateRolesForProject(project.id, payload);
    }
}

export async function fetchRoleTypes(product: string): Promise<Role[]> {
    if (product) {
        return await ProfileAdministrationAPI.getRoleTypesForProduct(product);
    } else {
        return [];
    }
}

export async function fetchRoles(project: Project): Promise<ProjectRole[]> {
    const store = useProductsWithRolesStore();
    await store.ensureLoaded();
    if (project.id && store.productHasRoles(project.product)) {
        const [roles, defaultNames] = await Promise.all([
            ProfileAdministrationAPI.getRolesForProject(project.id),
            fetchDefaultRoleNames(project),
        ]);

        return roles.map((role) => {
            const backendName = role.name?.trim() ?? "";
            if (backendName !== "") return role;

            return {
                ...role,
                name: resolveDefaultRoleName(defaultNames, role.role),
            };
        });
    } else {
        return [];
    }
}

export async function createParticipantAndLogActivity(projectId: number, participants: Participant[]) {
    const createdParticipants = await ProfileAdministrationAPI.addParticipants(projectId, {
        participants: participants,
    });

    // Awaited, so the history exists by the time the caller refreshes the table (an async callback
    // handed to forEach is fire-and-forget). A failed write is reported but must not fail the
    // creation: the participants are already saved, and callers treat a rejection as "not created".
    await Promise.all(
        createdParticipants.participants.map((participant) =>
            ProfileAdministrationAPI.logParticipantActivity(
                participant.id!,
                ParticipantActivityType.PARTICIPANT_CREATED,
            ).catch((error) => console.error(`Could not log the creation of participant ${participant.id}`, error)),
        ),
    );
}

/**
 * Activates a participant by sending the questionnaire email and updating the participant status
 * @param project The project the participant is part of
 * @param participant The participant to create the questionnaire for
 * @param trainer The trainer of the project
 */
export async function createQuestionnaire(
    project: Project,
    participant: Participant,
    trainer: Trainer,
): Promise<Participant> {
    try {
        if (participant.id === undefined) {
            throw new Error("participant id is missing");
        }
        const activatedParticipant = await ProfileAdministrationAPI.activateParticipant(participant.id);

        // Send the questionnaire email
        await sendQuestionnaireEmail(trainer, activatedParticipant, project);

        // Log the activity
        await ProfileAdministrationAPI.logParticipantActivity(
            participant.id,
            participant.status == "CREATED"
                ? ParticipantActivityType.QUESTIONNAIRE_MAIL_SENT
                : ParticipantActivityType.REMINDER_MAIL_SENT,
        );

        return activatedParticipant;
    } catch (error) {
        console.error("Error creating questionnaire", error);
        throw error;
    }
}

/**
 * Activates a participant by sending the questionnaire email and updating the participant status
 * @param participant The participant to activate
 * @returns
 */
export async function activateParticipant(project: Project, participant: Participant, trainer: Trainer) {
    try {
        // Validate the available contingent of the product
        const activatedParticipant: Participant = await createQuestionnaire(project, participant, trainer)

        return activatedParticipant;
    } catch (error) {
        console.error("Error activating participant", error);
        throw error;
    }
}

/**
 * Recalculates the profile for all generated participants in a project
 * @param projectID
 */
export async function recalculateProfileForAllParticipants(projectID: number) {
    const participants = await ProfileAdministrationAPI.getParticipants(projectID);
    await Promise.all(
        participants
            .filter((participant) => participant.status && ["GENERATED", "NOTIFIED"].includes(participant.status))
            .map((participant) => ProfileContractorAPI.recalculateProfileForParticipant(participant.id!)),
    );
}

/**
 * Fetches product names based on the current language setting.
 *
 * This function retrieves product identifiers and their corresponding texts from the Language Management API.
 * It attempts to find the product name in the current language, falling back to English if unavailable.
 *
 * @returns {Promise<Record<string, string>>} A promise that resolves to a key-value map of product identifiers and their translated names.
 *
 * @example
 * const productNames = await getProductNames();
 * console.log(productNames["in100"]); // Output: "Personality Factor Profile"
 */
export async function getProductNames(): Promise<Record<string, string>>;
export async function getProductNames(lang: string): Promise<Record<string, string>>;
export async function getProductNames(lang?: string): Promise<Record<string, string>> {
    const productIdentifiers: Identifier[] = await LanguageManagementAPI.fetchTexts("products");

    const productTranslations: Record<string, string> = {};
    const i18nLang = lang ?? i18n.global.locale.value;

    productIdentifiers.forEach((id) => {
        const productTextObj =
        id.texts.find((t) => t.language === i18nLang) ?? id.texts.find((t) => t.language === "en");

        productTranslations[id.identifier] = productTextObj?.text ?? id.identifier;
    });

    return productTranslations;
}
